import {useSearchParams} from 'react-router-dom'
import {CheckCircle, Clock, Play, XCircle} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Pagination} from '@/components/ui/pagination'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {routes} from '@/lib/routes'
import {MutedText} from '@/components/shared/MutedText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {Icon} from '@/components/shared/Icon'
import {formatTimestamp} from '@/lib/date-utils'
import {ProvisioningAction, ProvisioningTaskStatus} from '@/types/provisioning'
import type {
    ProvisioningTask,
    ProvisioningTaskHistory,
    ProvisioningAction as ProvisioningActionType,
    ProvisioningTaskStatus as ProvisioningTaskStatusType,
} from '@/types/provisioning'
import {
    useOutboundConnectorProvisioningTasks,
    useOutboundConnectorProvisioningHistory,
} from '@/hooks/use-outbound-connectors'

const ACTIVE_PAGE_PARAM = 'activePage'
const ACTIVE_SIZE_PARAM = 'activeSize'
const HISTORY_PAGE_PARAM = 'historyPage'
const HISTORY_SIZE_PARAM = 'historySize'
const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50]

interface Props {
    connectorId: string
    targetSystemId: string | null | undefined
}

export function ProvisioningTasksSubPage({connectorId, targetSystemId}: Props) {
    const i18n = useI18n()
    const [searchParams, setSearchParams] = useSearchParams()

    const activePage = parsePositiveInt(searchParams.get(ACTIVE_PAGE_PARAM), 0)
    const activeSize = parsePositiveInt(searchParams.get(ACTIVE_SIZE_PARAM), DEFAULT_PAGE_SIZE)
    const historyPage = parsePositiveInt(searchParams.get(HISTORY_PAGE_PARAM), 0)
    const historySize = parsePositiveInt(searchParams.get(HISTORY_SIZE_PARAM), DEFAULT_PAGE_SIZE)

    const updateParam = (key: string, value: number, defaultValue: number) => {
        const updated = new URLSearchParams(searchParams)
        if (value === defaultValue) {
            updated.delete(key)
        } else {
            updated.set(key, String(value))
        }
        return updated
    }

    const setActivePage = (next: number) => {
        setSearchParams(updateParam(ACTIVE_PAGE_PARAM, next, 0), {replace: true})
    }
    const setActiveSize = (next: number) => {
        const updated = updateParam(ACTIVE_SIZE_PARAM, next, DEFAULT_PAGE_SIZE)
        updated.delete(ACTIVE_PAGE_PARAM)
        setSearchParams(updated, {replace: true})
    }
    const setHistoryPage = (next: number) => {
        setSearchParams(updateParam(HISTORY_PAGE_PARAM, next, 0), {replace: true})
    }
    const setHistorySize = (next: number) => {
        const updated = updateParam(HISTORY_SIZE_PARAM, next, DEFAULT_PAGE_SIZE)
        updated.delete(HISTORY_PAGE_PARAM)
        setSearchParams(updated, {replace: true})
    }

    /* eslint-disable i18next/no-literal-string -- query params */
    const activeQuery = `page=${activePage}&size=${activeSize}&sort=createdAt,desc`
    const historyQuery = `page=${historyPage}&size=${historySize}&sort=completedAt,desc`
    /* eslint-enable i18next/no-literal-string */

    const activeQ = useOutboundConnectorProvisioningTasks(connectorId, activeQuery)
    const historyQ = useOutboundConnectorProvisioningHistory(connectorId, historyQuery)

    const activePageData = activeQ.data
    const historyPageData = historyQ.data
    const activeRows = activePageData?.content ?? []
    const historyRows = historyPageData?.content ?? []

    /* eslint-disable i18next/no-literal-string -- URL query param */
    const viewAllHref = targetSystemId
        ? `${routes.provisioning.history.path()}?targetSystemId=${targetSystemId}`
        : routes.provisioning.history.path()
    /* eslint-enable i18next/no-literal-string */

    return (
        <Stack className="mt-4">
            <Card data-testid="provisioning-tasks-active-card">
                <CardHeader>
                    <CardTitle data-testid="card-title-provisioning-tasks-active">{i18n.connectors.provisioningTasks.active}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {activeQ.isLoading ? (
                        <MutedText className="p-4" data-testid="provisioning-tasks-active-loading">
                            {i18n.connectors.provisioningTasks.loading}
                        </MutedText>
                    ) : activeQ.error ? (
                        <Stack className="p-4">
                            <ErrorBanner error={activeQ.error as Error}/>
                        </Stack>
                    ) : activeRows.length === 0 ? (
                        <InlineEmptyText data-testid="provisioning-tasks-active-empty">
                            {i18n.connectors.provisioningTasks.activeEmpty}
                        </InlineEmptyText>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-conn-prov-identity">{i18n.connectors.provisioningTasks.columns.identity}</TableHead>
                                    <TableHead data-testid="col-conn-prov-entitlement">{i18n.connectors.provisioningTasks.columns.entitlement}</TableHead>
                                    <TableHead data-testid="col-conn-prov-account">{i18n.connectors.provisioningTasks.columns.account}</TableHead>
                                    <TableHead data-testid="col-conn-prov-action">{i18n.connectors.provisioningTasks.columns.action}</TableHead>
                                    <TableHead data-testid="col-conn-prov-status">{i18n.connectors.provisioningTasks.columns.status}</TableHead>
                                    <TableHead data-testid="col-conn-prov-retries">{i18n.connectors.provisioningTasks.columns.retries}</TableHead>
                                    <TableHead data-testid="col-conn-prov-created">{i18n.connectors.provisioningTasks.columns.created}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {activeRows.map((row) => (
                                    <ActiveTaskRow key={row.id} row={row} i18n={i18n}/>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            {activePageData && activePageData.totalPages > 1 && (
                <Pagination
                    page={activePageData.number}
                    totalPages={activePageData.totalPages}
                    totalItems={activePageData.totalElements}
                    pageSize={activeSize}
                    onPageChange={setActivePage}
                    onPageSizeChange={setActiveSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
            )}

            <Card data-testid="provisioning-tasks-history-card">
                <CardHeader>
                    <CardTitle data-testid="card-title-provisioning-tasks-history">{i18n.connectors.provisioningTasks.history}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {historyQ.isLoading ? (
                        <MutedText className="p-4" data-testid="provisioning-tasks-history-loading">
                            {i18n.connectors.provisioningTasks.loading}
                        </MutedText>
                    ) : historyQ.error ? (
                        <Stack className="p-4">
                            <ErrorBanner error={historyQ.error as Error}/>
                        </Stack>
                    ) : historyRows.length === 0 ? (
                        <InlineEmptyText data-testid="provisioning-tasks-history-empty">
                            {i18n.connectors.provisioningTasks.historyEmpty}
                        </InlineEmptyText>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-conn-prov-hist-identity">{i18n.connectors.provisioningTasks.columns.identity}</TableHead>
                                    <TableHead data-testid="col-conn-prov-hist-entitlement">{i18n.connectors.provisioningTasks.columns.entitlement}</TableHead>
                                    <TableHead data-testid="col-conn-prov-hist-action">{i18n.connectors.provisioningTasks.columns.action}</TableHead>
                                    <TableHead data-testid="col-conn-prov-hist-status">{i18n.connectors.provisioningTasks.columns.status}</TableHead>
                                    <TableHead data-testid="col-conn-prov-hist-completed">{i18n.connectors.provisioningTasks.columns.completed}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {historyRows.map((row) => (
                                    <HistoryRow key={row.id} row={row} i18n={i18n}/>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            {historyPageData && historyPageData.totalPages > 1 && (
                <Pagination
                    page={historyPageData.number}
                    totalPages={historyPageData.totalPages}
                    totalItems={historyPageData.totalElements}
                    pageSize={historySize}
                    onPageChange={setHistoryPage}
                    onPageSizeChange={setHistorySize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
            )}

            <InlineRow justify="end">
                <TextLink
                    to={viewAllHref}
                    testId="provisioning-tasks-view-all"
                    size="sm"
                >
                    {i18n.connectors.provisioningTasks.viewAll}
                </TextLink>
            </InlineRow>
        </Stack>
    )
}

function ActiveTaskRow({row, i18n}: {row: ProvisioningTask; i18n: ReturnType<typeof useI18n>}) {
    return (
        // read-only
        <TableRow data-readonly data-testid={`provisioning-tasks-active-row-${row.id}`}>
            <TableCell><EntityLinkChip value={row.identity}/></TableCell>
            <TableCell>{row.enforcementObject ? <EntityLinkChip value={row.enforcementObject}/> : '—'}</TableCell>
            <TableCell>{row.accountExternalIdentifier ?? '—'}</TableCell>
            <TableCell>{actionBadge(row.action, i18n)}</TableCell>
            <TableCell>{statusBadge(row.status, i18n)}</TableCell>
            <TableCell>{row.retryCount}/{row.maxRetries}</TableCell>
            <TableCell>{formatTimestamp(row.createdAt)}</TableCell>
        </TableRow>
    )
}

function HistoryRow({row, i18n}: {row: ProvisioningTaskHistory; i18n: ReturnType<typeof useI18n>}) {
    return (
        // read-only
        <TableRow data-readonly data-testid={`provisioning-tasks-history-row-${row.id}`}>
            <TableCell><EntityLinkChip value={row.identity}/></TableCell>
            <TableCell>{row.enforcementObject ? <EntityLinkChip value={row.enforcementObject}/> : '—'}</TableCell>
            <TableCell>{actionBadge(row.action, i18n)}</TableCell>
            <TableCell>{statusBadge(row.status, i18n)}</TableCell>
            <TableCell>{formatTimestamp(row.completedAt)}</TableCell>
        </TableRow>
    )
}

function statusBadge(status: ProvisioningTaskStatusType, i18n: ReturnType<typeof useI18n>) {
    switch (status) {
        case ProvisioningTaskStatus.RUNNING:
            return <Badge variant={BadgeVariant.SUCCESS}><Icon icon={Play} size="xs" className="mr-1"/>{i18n.provisioning.running}</Badge>
        case ProvisioningTaskStatus.FAILED:
            return <Badge variant="destructive"><Icon icon={XCircle} size="xs" className="mr-1"/>{i18n.provisioning.failed}</Badge>
        case ProvisioningTaskStatus.QUEUED:
            return <Badge variant="outline"><Icon icon={Clock} size="xs" className="mr-1"/>{i18n.provisioning.queued}</Badge>
        case ProvisioningTaskStatus.DONE:
            return <Badge variant={BadgeVariant.SUCCESS}><Icon icon={CheckCircle} size="xs" className="mr-1"/>{i18n.provisioning.done}</Badge>
        case ProvisioningTaskStatus.CANCELED:
            return <Badge variant="outline"><Icon icon={XCircle} size="xs" className="mr-1"/>{i18n.provisioning.canceled}</Badge>
        default:
            return <Badge variant="secondary">{status}</Badge>
    }
}

function actionBadge(action: ProvisioningActionType, i18n: ReturnType<typeof useI18n>) {
    switch (action) {
        case ProvisioningAction.GRANT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionGrant}</Badge>
        case ProvisioningAction.REVOKE:
            return <Badge variant="outline">{i18n.provisioning.actionRevoke}</Badge>
        case ProvisioningAction.CREATE_ACCOUNT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionCreateAccount}</Badge>
        case ProvisioningAction.ENABLE_ACCOUNT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionEnableAccount}</Badge>
        case ProvisioningAction.DISABLE_ACCOUNT:
            return <Badge variant="warning">{i18n.provisioning.actionDisableAccount}</Badge>
        case ProvisioningAction.DELETE_ACCOUNT:
            return <Badge variant="destructive">{i18n.provisioning.actionDeleteAccount}</Badge>
        case ProvisioningAction.UPDATE_ACCOUNT:
            return <Badge variant="secondary">{i18n.provisioning.actionUpdateAccount}</Badge>
        default:
            return <Badge variant="secondary">{action}</Badge>
    }
}

function parsePositiveInt(value: string | null, fallback: number): number {
    if (!value) return fallback
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed) || parsed < 0) return fallback
    return parsed
}
